<template>
    <div class="sbs-slider">
        <div class="sbs-slider__main">
            <div
                :id="sliderId"
                class="swiper-container"
                :class="swiperContainerClasses">
                <SbsDraggable
                    v-bind="swiperWrapperData"
                    v-on="swiperWrapperListeners"
                    class="swiper-wrapper">
                    <!-- @slot Слот по-умолчанию -->
                    <slot></slot>
                </SbsDraggable>

                <!-- @slot Слот для кнопки перелистывания назад -->
                <slot name="navigationPrev"></slot>

                <!-- @slot Слот для кнопки перелистывания вперед -->
                <slot name="navigationNext"></slot>

                <!-- @slot Слот для элементов пагинации -->
                <slot name="pagination"></slot>
            </div>

            <!-- @slot Слот для описания под изображением -->
            <slot name="description"></slot>
        </div>

        <div
            v-if="thumbs"
            :id="sliderThumbsId"
            class="swiper-container">
            <div class="swiper-wrapper">
                <!-- @slot Слот для галереи превью -->
                <slot name="thumbs"></slot>
            </div>

            <!-- @slot Слот для кнопки перелистывания назад в галерее превью -->
            <slot name="thumbsNavigationPrev"></slot>

            <!-- @slot Слот для кнопки перелистывания вперед в галерее превью -->
            <slot name="thumbsNavigationNext"></slot>
        </div>
    </div>
</template>

<script>
import 'swiper/swiper-bundle.css';
import { Swiper } from 'swiper/swiper-bundle.esm.js';
import _merge from 'lodash/merge.js';
import { getRandomId } from '../../utils/helpers.js';
import SbsDraggable from '../draggable/SbsDraggable.vue';

export default {
    name: 'SbsSlider',
    components: {
        SbsDraggable,
    },
    data() {
        return {
            swiper: null,
            swiperThumbs: null,
        };
    },
    computed: {
        sliderId() {
            return this.id || `sbs-slider-${getRandomId()}`;
        },
        sliderThumbsId() {
            return `${this.sliderId}-thumbs`;
        },
        swiperContainerClasses() {
            return [this.swiperContainerClass];
        },
        swiperDefaultOptions() {
            return {
                allowTouchMove: !this.draggable,
                createElements: true,
                grabCursor: !this.draggable,
                observer: true,
                resizeObserver: true,
                slidesPerView: 'auto',
                touchEventsTarget: 'wrapper',
                thumbs: {
                    swiper: this.swiperThumbs,
                },
            };
        },
        sliderOptions() {
            return _merge(
                this.swiperDefaultOptions,
                this.swiperOptions,
            );
        },
        swiperThumbsDefaultOptions() {
            return {
                centerInsufficientSlides: true,
                grabCursor: true,
                slidesPerView: 'auto',
                watchSlidesProgress: true,
            };
        },
        sliderThumbsOptions() {
            return _merge(
                this.swiperThumbsDefaultOptions,
                this.swiperThumbsOptions,
            );
        },
        swiperWrapperData() {
            return this.draggable
                ? this.draggableOptions
                : { disabled: true };
        },
        swiperWrapperListeners() {
            return this.draggable
                ? this.draggableListeners
                : {};
        },
    },
    props: {
        /**
         * Включает возможность менять слайды местами с помощью драг-н-дропа.
         * При включении этой опции будет отключена возможность переключать слайды свайпом.
         */
        draggable: {
            type: Boolean,
            default: false,
        },
        /**
         * Объект со слушателями событий `SbsDraggable`
         */
        draggableListeners: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Объект с настройками `SbsDraggable`
         */
        draggableOptions: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Уникальный идентификатор слайдера
         */
        id: {
            type: String,
            default: '',
        },
        /**
         * Класс для контейнера слайдера
         */
        swiperContainerClass: {
            type: String,
            default: '',
        },
        /**
         * Опции для слайдера Swiper
         * Список всех опций можно посмотреть [здесь](https://swiperjs.com/swiper-api)
         */
        swiperOptions: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Опции галереи превью слайдера Swiper
         */
        swiperThumbsOptions: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Активация отображения превью галереи слайдера
         */
        thumbs: {
            type: Boolean,
            default: false,
        },
    },
    async mounted() {
        await this.$nextTick();

        if (this.thumbs) {
            this.swiperThumbs = new Swiper(`#${this.sliderThumbsId}`, this.sliderThumbsOptions);
        }

        this.swiper = new Swiper(`#${this.sliderId}`, this.sliderOptions);
        this.$emit('init', this.swiper);
    },
};
</script>

<style lang="scss">
.sbs-slider {
    display: flex;
    flex-direction: column;

    &__main {
        display: flex;
        flex-grow: 1;
    }

    .swiper-container {
        width: 100%;
    }

    .swiper-wrapper {
        & > * {
            box-sizing: border-box;
        }
    }
}
</style>
